import { useState } from "react";
import type React from "react";

import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslation } from "react-i18next";

import { useApiClient } from "api/useApiClient";
import { useProfileContext } from "contexts/ProfileContext";
import { useSnackbarContext } from "contexts/SnackbarContext";
import { IconButton as CustomIconButton } from "shared/atoms/IconButton";
import { useLocalizedDate } from "utils/date";

import { generateProtocolPDF } from "./generateProtocolPDF";

type DownloadProtocolButtonProps = {
  patientId: number;
  patientName: string;
  patientPersonalNumber: string | null;
  patientBirthday: string | null;
  scheduleVersion: number | undefined | null;
};

export const DownloadProtocolButton: React.FC<DownloadProtocolButtonProps> = ({
  patientId,
  patientName,
  patientPersonalNumber,
  patientBirthday,
  scheduleVersion,
}) => {
  const { t } = useTranslation();
  const client = useApiClient();
  const { profile } = useProfileContext();
  const { format, formats } = useLocalizedDate();
  const { showSnackbar } = useSnackbarContext();

  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsLoading(true);

    try {
      await generateProtocolPDF({
        client,
        patientId,
        translations: {
          reps: t("library.exercises.fields.reps"),
          sets: t("library.exercises.fields.sets"),
          levelNotFound: t("library.exercises.form.level_not_found"),
        },
        patientInfo: {
          name: patientName,
          identifierLabel: patientPersonalNumber
            ? t("patients.medical_records.personal_number")
            : t("patients.extended_medical_note.patient_birthday.label"),
          identifier: patientPersonalNumber ?? patientBirthday,
          therapistName: [profile?.first_name, profile?.last_name].filter(Boolean).join(" "),
          clinicName: profile?.therapist_profile?.branded_health_care_provider?.name ?? "",
        },
        formattedDate: format(new Date(), formats.LONG_LOCALIZED_DATE_PP),
        scheduleVersion,
      });
    } catch (error) {
      showSnackbar({
        message: t("patients.protocol.download_protocol_failed"),
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CustomIconButton
      label={t("patients.protocol.download_protocol")}
      hideLabel="belowTablet"
      onClick={handleClick}
      disabled={isLoading}
    >
      {isLoading ? <CircularProgress size={20} /> : <FileDownloadIcon />}
    </CustomIconButton>
  );
};
